import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { Loader2, Lock, Mail, ShieldCheck, KeyRound } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

const formatErr = (d) => (typeof d === "string" ? d : Array.isArray(d) ? d.map((e) => e?.msg).join(" ") : "Error");

export default function AdminPanelLoginPage() {
  const { user, loading, login, loginWithCode, logout, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("credentials"); // "credentials" | "code"
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [remember, setRemember] = useState(true);

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 text-[#d4af37] animate-spin" /></div>;
  }
  if (user && isAdmin) return <Navigate to="/admin" replace />;

  const submitCredentials = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const u = await login(identifier.trim(), password, remember);
      if (u.role !== "admin") {
        await logout();
        toast.error("Esta cuenta no tiene permisos de administrador");
        return;
      }
      toast.success("Acceso concedido");
      navigate("/admin");
    } catch (err) {
      toast.error(formatErr(err?.response?.data?.detail) || "Credenciales inválidas");
    } finally { setSubmitting(false); }
  };

  const submitCode = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await loginWithCode(code.trim(), remember);
      toast.success("Acceso concedido");
      navigate("/admin");
    } catch (err) {
      toast.error(formatErr(err?.response?.data?.detail) || "Código incorrecto");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6 py-12 relative" data-testid="admin-panel-login">
      <div className="absolute inset-0 bg-gradient-to-br from-black via-[#0a0000] to-[#1a0000]/40 pointer-events-none" />
      <div className="absolute top-10 left-10 w-32 h-32 rounded-full bg-[#d4af37]/8 blur-3xl pointer-events-none" />
      <div className="absolute bottom-10 right-10 w-40 h-40 rounded-full bg-[#5A0001]/20 blur-3xl pointer-events-none" />

      <div className="relative glass-dark border border-[#d4af37]/40 rounded-2xl p-8 lg:p-10 max-w-md w-full">
        <div className="text-center mb-7">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#d4af37]/15 border border-[#d4af37]/40 mb-3">
            <ShieldCheck className="w-8 h-8 text-[#d4af37]" />
          </div>
          <h1 className="font-serif text-3xl text-white mb-1">Panel Restringido</h1>
          <p className="text-xs uppercase tracking-[0.25em] text-[#d4af37]">Solo Administradores</p>
        </div>

        {/* Tabs */}
        <div className="grid grid-cols-2 bg-black/40 border border-white/10 rounded-full p-1 mb-6">
          <button
            data-testid="tab-credentials"
            type="button"
            onClick={() => setTab("credentials")}
            className={`py-2.5 text-[11px] uppercase tracking-widest rounded-full transition-all flex items-center justify-center gap-1.5 ${tab === "credentials" ? "bg-[#d4af37] text-black" : "text-neutral-400"}`}
          >
            <Lock className="w-3 h-3" /> Credenciales
          </button>
          <button
            data-testid="tab-code"
            type="button"
            onClick={() => setTab("code")}
            className={`py-2.5 text-[11px] uppercase tracking-widest rounded-full transition-all flex items-center justify-center gap-1.5 ${tab === "code" ? "bg-[#d4af37] text-black" : "text-neutral-400"}`}
          >
            <KeyRound className="w-3 h-3" /> Código Secreto
          </button>
        </div>

        {tab === "credentials" ? (
          <form onSubmit={submitCredentials} className="space-y-4">
            <div>
              <Label className="text-neutral-300 flex items-center gap-2 mb-2 text-xs uppercase tracking-widest">
                <Mail className="w-3 h-3 text-[#d4af37]" /> Usuario
              </Label>
              <Input
                data-testid="admin-username"
                required
                autoComplete="username"
                value={identifier}
                onChange={(e) => setIdentifier(e.target.value)}
                className="bg-black/40 border-white/10 text-white"
              />
            </div>
            <div>
              <Label className="text-neutral-300 flex items-center gap-2 mb-2 text-xs uppercase tracking-widest">
                <Lock className="w-3 h-3 text-[#d4af37]" /> Contraseña
              </Label>
              <Input
                data-testid="admin-password"
                type="password"
                required
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="bg-black/40 border-white/10 text-white"
              />
            </div>
            <label className="flex items-start gap-2 cursor-pointer select-none" data-testid="admin-remember-wrap">
              <input type="checkbox" data-testid="admin-remember" checked={remember} onChange={(e) => setRemember(e.target.checked)} className="mt-0.5 accent-[#d4af37]" />
              <span className="text-[11px] text-neutral-300 leading-snug">Mantener la sesión abierta en este dispositivo<br/><span className="text-neutral-500">Reconoceremos este navegador y no volverá a pedir contraseña hasta que cierres sesión.</span></span>
            </label>
            <button
              data-testid="admin-submit"
              type="submit"
              disabled={submitting}
              className="btn-gold w-full py-3.5 rounded-full uppercase text-xs tracking-widest disabled:opacity-60"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin inline" /> : "Acceder al Panel"}
            </button>
          </form>
        ) : (
          <form onSubmit={submitCode} className="space-y-4">
            <div>
              <Label className="text-neutral-300 flex items-center gap-2 mb-2 text-xs uppercase tracking-widest">
                <KeyRound className="w-3 h-3 text-[#d4af37]" /> Código Secreto
              </Label>
              <Input
                data-testid="admin-code-input"
                type="password"
                required
                autoComplete="off"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder="Ingresa tu código"
                className="bg-black/40 border-white/10 text-white text-center font-mono tracking-widest"
              />
              <p className="text-[10px] text-neutral-500 mt-2 italic text-center">
                Con el código secreto no se necesita usuario ni contraseña.
              </p>
            </div>
            <label className="flex items-start gap-2 cursor-pointer select-none" data-testid="admin-remember-code-wrap">
              <input type="checkbox" data-testid="admin-remember-code" checked={remember} onChange={(e) => setRemember(e.target.checked)} className="mt-0.5 accent-[#d4af37]" />
              <span className="text-[11px] text-neutral-300 leading-snug">Mantener la sesión abierta en este dispositivo<br/><span className="text-neutral-500">Reconoceremos este navegador y no volverá a pedir contraseña hasta que cierres sesión.</span></span>
            </label>
            <button
              data-testid="admin-code-submit"
              type="submit"
              disabled={submitting || !code.trim()}
              className="btn-gold w-full py-3.5 rounded-full uppercase text-xs tracking-widest disabled:opacity-60"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin inline" /> : "Acceder con Código"}
            </button>
          </form>
        )}

        <p className="text-center text-[10px] text-neutral-500 mt-6 italic">
          Acceso reservado al equipo administrativo de CrisAngel Astral
        </p>
      </div>
    </div>
  );
}
